import { Fragment } from 'react';
import { Dropdown } from '../../components/Dropdown';
import { ErrorText } from '../../components/ErrorText';
import { InfoNote } from '../../components/InfoNote';
import { SectionHeader } from '../../components/SectionHeader';
import { TimeChangeIcon } from '../../icons/TimeChangeIcon';
import {
  OptionCard,
  OptionImage,
  OptionImagePlaceholder,
  OptionLabel,
  OptionsSection,
  Panel,
  SectionError,
  SelectionCount,
  TimeslotError,
  TimeslotHeading,
  TimeslotPanel,
} from './MultiOptionTimeslotPanel.styles';
import type { MultiOptionTimeslotPanelProps } from './MultiOptionTimeslotPanel.types';

const defaultNoteIcon = <TimeChangeIcon size={18} />;

export function MultiOptionTimeslotPanel({
  title = 'Options.',
  subtitle = 'Choose your times.',
  options,
  maximumSelections,
  selectionCountText,
  onOptionToggle,
  onTimeslotChange,
  noteText,
  noteIcon = defaultNoteIcon,
  timeslotPlaceholder = 'Time*',
  timeslotHeading = 'What time would you like?',
  timeslotAriaLabelFormatter = (l) => `${l} time`,
  optionError,
  timeslotErrors,
}: MultiOptionTimeslotPanelProps) {
  const selectedCount = options.filter((o) => o.isSelected).length;
  const atMax = maximumSelections !== undefined && selectedCount >= maximumSelections;

  return (
    <Panel>
      <SectionHeader title={title} subtitle={subtitle} />
      {selectionCountText && (
        <SelectionCount aria-live="polite">{selectionCountText}</SelectionCount>
      )}
      {optionError && (
        <SectionError data-error="true" aria-invalid="true" tabIndex={-1}>
          <ErrorText>{optionError}</ErrorText>
        </SectionError>
      )}
      <OptionsSection>
        {options.map((option) => {
          const isDisabled = !!option.disabled || (atMax && !option.isSelected);
          const showTimeslotPanel = option.isSelected && option.timeslots.length > 0;
          const timeslotErrorText = timeslotErrors?.[option.id];

          return (
            <Fragment key={option.id}>
              <OptionCard
                type="button"
                data-selected={option.isSelected}
                data-disabled={isDisabled}
                aria-pressed={option.isSelected}
                aria-disabled={isDisabled}
                disabled={isDisabled}
                onClick={() => onOptionToggle(option.id)}
              >
                <OptionLabel>{option.label}</OptionLabel>
                {option.imageUrl ? (
                  <OptionImage src={option.imageUrl} alt={option.imageAlt || option.label} />
                ) : (
                  <OptionImagePlaceholder />
                )}
              </OptionCard>
              {showTimeslotPanel && (
                <TimeslotPanel>
                  <TimeslotHeading>{timeslotHeading}</TimeslotHeading>
                  <Dropdown
                    options={option.timeslots}
                    value={option.selectedTimeslot}
                    placeholder={timeslotPlaceholder}
                    onChange={(value) => onTimeslotChange(option.id, value)}
                    aria-label={timeslotAriaLabelFormatter(option.label)}
                    hasError={!!timeslotErrorText}
                  />
                  {timeslotErrorText && (
                    <TimeslotError>
                      <ErrorText>{timeslotErrorText}</ErrorText>
                    </TimeslotError>
                  )}
                  {noteText && <InfoNote text={noteText} icon={noteIcon} />}
                </TimeslotPanel>
              )}
            </Fragment>
          );
        })}
      </OptionsSection>
    </Panel>
  );
}
